// Typed client for portal.rsmech.org/api/v1
// Talks to /api/* on the Cloudflare Pages Function, which proxies with Bearer auth.
// (Browser never sees RSME_API_KEY.)

export type DateStr = string; // "DD/MM/YYYY" or "YYYY-MM-DD"

export interface Pagination { page: number; limit: number; total: number; }
export interface Envelope<T> { data: T[]; pagination?: Pagination; }

export interface Asset {
  id: number;
  assetName: string;
  brand?: string;
  model?: string;
  tonnage?: string;
  lta?: string;
  engine?: string;
  forkLength?: string;
  liftingHeight?: string;
  closedMastHeight?: string;
  mastType?: string;
  attachment?: string;
  entity?: 'RSM' | 'TOS' | 'HSS';
  status: 'available' | 'rented' | 'maintenance' | 'sold' | 'reserved';
  underRepair?: 0 | 1;
  createdAt?: string;
}

export interface FleetQueryResponse {
  counts: { available: number; unavailable: number; reserved: number; underRepair: number; };
  appliedFilters: Record<string, unknown>;
  available: Asset[];
  unavailable: Array<Asset & { booking?: { from: DateStr; to: DateStr } }>;
  underRepair: Array<Asset & { nextBooking?: { from: DateStr; to: DateStr } }>;
  assets: Asset[];
}

export interface Order {
  id: number;
  orderNumber: string;
  assetName?: string;
  company?: string;
  rentOutDate?: DateStr;
  returnDate?: DateStr;
  tonnage?: string;
  model?: string;
  lta?: string;
  poNo?: string;
  quotationNo?: string;
  doNo?: string;
  rate?: number;
  term?: string;
  status: 'active' | 'completed' | 'cancelled' | 'reserved';
  salesPerson?: string;
  workingLocation?: string;
  contactPerson?: string;
  contactNumber?: string;
  remarks?: string;
}

export interface Customer {
  id: number;
  clientCode?: string;
  companyName: string;
  address?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
  billingEntity?: string;
}

export interface Contact {
  id: number;
  customerId: number;
  companyName?: string;
  name: string;
  phone?: string;
  email?: string;
  designation?: string;
  isPrimary?: boolean;
}

export interface RepairTicket {
  id: number;
  ticketNo: string;
  assetType?: 'our_forklift' | 'customer_forklift' | 'prime_mover';
  assetName: string;
  ltaPlate?: string;
  description: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  serviceType?: 'contract' | 'warranty' | 'rental' | 'general' | 'repair' | 'general_service';
  assignedMechanic?: string;
  reportedBy?: string;
  isApproved?: boolean;
  approvedAt?: string;
  estimatedRepairDate?: DateStr;
  createdAt?: string;
}

export interface Quotation {
  id: number;
  quotationNo: string;
  companyName: string;
  subject?: string;
  totalAmount?: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  dateOfComm?: DateStr;
  validity?: string;
  salesPerson?: string;
}

export interface DeliveryOrder {
  id: number;
  doRefNo: string;
  customerName: string;
  assetName?: string;
  model?: string;
  tonnage?: string;
  deliveryAddress?: string;
  status: 'draft' | 'issued' | 'delivered' | 'returned';
  issuedAt?: string;
  deliveredAt?: string;
  returnedAt?: string;
  pdfUrl?: string;
}

export interface Lead {
  id: number;
  name: string;
  company?: string;
  phone?: string;
  email?: string;
  source?: string;
  notes?: string;
  tonnageRequired?: string;
  rentalPeriod?: string;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  createdAt?: string;
}

export interface Mechanic { id: number; name: string; phone?: string; specialisation?: string; }
export interface PrimeMover { id: number; ltaPlate: string; brand?: string; model?: string; year?: number; status?: string; assignedDriver?: string; remarks?: string; }
export interface RateTier { id: number; tierName: string; tonnage: number; monthlyRate?: number; weeklyRate?: number; dailyRate?: number; }
export interface Certificate { id: number; assetName: string; certType: string; certNumber?: string; issuedDate?: DateStr; coverageEnd?: DateStr; issuedBy?: string; notes?: string; }
export interface PdiRequest { id: number; assetName: string; orderId?: number; customerId?: number; scheduledDate?: DateStr; status: 'pending' | 'in_progress' | 'completed'; assignedMechanic?: string; notes?: string; }
export interface OffHireRecord { id: number; assetName: string; mechanic: string; inspectionDate: DateStr; }
export interface Staff { id: number; username: string; displayName?: string; jobTitle?: string; email?: string; phone?: string; role?: string; }
export interface RevenueRow { id: number; month: number; year: number; entity?: string; amount: number; notes?: string; }
export interface PmInvoice { id: number; ticketId: number; invoiceNo?: string; invoiceDate?: DateStr; totalAmount?: number; status?: string; notes?: string; }
export interface OtSubmission { id: number; staffName: string; date: DateStr; hoursOT?: number; reason?: string; approvedBy?: string; status?: string; }
export interface SalesKnowledge { id: number; title: string; content: string; category?: string; tags?: string[]; active?: boolean; }
export interface AssetDocument { id: number; assetName: string; docType: string; docUrl: string; fileName?: string; notes?: string; }
export interface InsurancePolicy { id: number; policyNumber?: string; insurer?: string; coverageType?: string; startDate?: DateStr; endDate?: DateStr; premium?: number; notes?: string; }
export interface FleetInsuranceCoverage { id: number; assetName: string; policyNumber?: string; insurer?: string; coverageType?: string; startDate?: DateStr; endDate?: DateStr; premium?: number; notes?: string; }
export interface Setting { key: string; value: string; description?: string; }

export interface FleetQueryFilters {
  tonnage?: number;
  tonnageMax?: number;
  tonnageExact?: number;
  forkLength?: number;
  brand?: string;
  model?: string;
  engine?: string;
  mastType?: string;
  attachment?: string;
  entity?: 'RSM' | 'TOS' | 'HSS';
  status?: 'available' | 'rented' | 'under_repair' | 'reserved' | 'all';
  date?: DateStr;
  endDate?: DateStr;
}

type Q = Record<string, string | number | boolean | undefined | null>;

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`/api${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    throw new ApiError(0, `Network error reaching ${path}. The backend proxy is not running — in dev start \`wrangler pages dev dist\`, in prod set RSME_API_KEY.`, e);
  }
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    let body: unknown = text;
    try { body = JSON.parse(text); } catch { /* keep as text */ }
    throw new ApiError(r.status, `${init.method ?? 'GET'} ${path} ${r.status}: ${text.slice(0, 200)}`, body);
  }
  return r.status === 204 ? (undefined as T) : await r.json();
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: unknown) { super(message); }
}

function qs(q: Q): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

// ============ FLEET & ASSETS ============
export const assets = {
  list: (q: { search?: string; status?: Asset['status']; page?: number; limit?: number } = {}) =>
    api<Envelope<Asset>>(`/assets${qs(q)}`),
  get: (id: number) => api<Asset>(`/assets/${id}`),
  create: (body: Partial<Asset>) => api<Asset>('/assets', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/assets/${id}`, { method: 'DELETE' }),
  availability: (id: number, startDate: DateStr, endDate: DateStr) =>
    api<{ available: boolean; conflictingOrders: Order[] }>(`/assets/${id}/availability${qs({ startDate, endDate })}`),
  byCustomer: (customerId: number) => api<Asset[]>(`/assets/by-customer/${customerId}`),
};

export const fleet = {
  query: (filters: FleetQueryFilters = {}) => api<FleetQueryResponse>(`/fleet/query${qs(filters as Q)}`),
};

// ============ ORDERS ============
export const orders = {
  list: (q: { search?: string; status?: Order['status']; page?: number; limit?: number } = {}) =>
    api<Envelope<Order>>(`/orders${qs(q)}`),
  get: (id: number) => api<Order>(`/orders/${id}`),
  create: (body: Partial<Order>) => api<Order>('/orders', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/orders/${id}`, { method: 'DELETE' }),
  byCustomer: (customerId: number) => api<Envelope<Order>>(`/orders/by-customer/${customerId}`),
  pdi: (id: number) => api<PdiRequest | null>(`/orders/${id}/pdi`),
  pipelineGet: (id: number) => api<Array<{ step: string; done: boolean }>>(`/orders/${id}/pipeline`),
  pipelineSet: (id: number, step: string, done: boolean) =>
    api<void>(`/orders/${id}/pipeline`, { method: 'PUT', body: JSON.stringify({ step, done }) }),
};

// ============ CUSTOMERS ============
export const customers = {
  list: (q: { search?: string; page?: number; limit?: number } = {}) =>
    api<Envelope<Customer>>(`/customers${qs(q)}`),
  get: (id: number) => api<Customer>(`/customers/${id}`),
  create: (body: Partial<Customer>) => api<Customer>('/customers', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/customers/${id}`, { method: 'DELETE' }),
  byClientCode: (code: string) => api<Customer>(`/customers/id/${encodeURIComponent(code)}`),
  byPhone: (phone: string) => api<Customer[]>(`/customers/phone/${encodeURIComponent(phone)}`),
};

// ============ CONTACTS ============
export const contacts = {
  list: (q: { search?: string } = {}) => api<Envelope<Contact>>(`/contacts${qs(q)}`),
  get: (id: number) => api<Contact>(`/contacts/${id}`),
  create: (body: Partial<Contact>) => api<Contact>('/contacts', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Contact>) => api<Contact>(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/contacts/${id}`, { method: 'DELETE' }),
};

// ============ REPAIR TICKETS ============
export const repairTickets = {
  list: (q: { search?: string; status?: RepairTicket['status'] } = {}) =>
    api<Envelope<RepairTicket>>(`/repair-tickets${qs(q)}`),
  get: (id: number) => api<RepairTicket & { records?: RepairRecord[] }>(`/repair-tickets/${id}`),
  create: (body: Partial<RepairTicket>) =>
    api<RepairTicket>('/repair-tickets', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<RepairTicket>) =>
    api<RepairTicket>(`/repair-tickets/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setStatus: (id: number, status: RepairTicket['status']) =>
    api<RepairTicket>(`/repair-tickets/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  remove: (id: number) => api<void>(`/repair-tickets/${id}`, { method: 'DELETE' }),
  records: (id: number) => api<RepairRecord[]>(`/repair-tickets/${id}/records`),
  addRecord: (id: number, body: Partial<RepairRecord>) =>
    api<RepairRecord>(`/repair-tickets/${id}/records`, { method: 'POST', body: JSON.stringify(body) }),
  addPart: (id: number, body: { recordId: number; partName: string; partNumber?: string; quantity?: number; unitCost?: number }) =>
    api<void>(`/repair-tickets/${id}/parts`, { method: 'POST', body: JSON.stringify(body) }),
};

export interface RepairRecord {
  id: number;
  description: string;
  workDone?: string;
  technicianName?: string;
  hoursSpent?: number;
  recordDate?: DateStr;
}

// ============ QUOTATIONS ============
export const quotations = {
  list: (q: { search?: string; status?: Quotation['status'] } = {}) =>
    api<Envelope<Quotation>>(`/quotations${qs(q)}`),
  get: (id: number) => api<Quotation>(`/quotations/${id}`),
  nextNumber: (salesPerson?: string) => api<{ quotationNo: string }>(`/quotations/next-number${qs({ salesPerson })}`),
  create: (body: Partial<Quotation> & { lineItems?: unknown[] }) =>
    api<Quotation>('/quotations', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Quotation>) =>
    api<Quotation>(`/quotations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/quotations/${id}`, { method: 'DELETE' }),
  generatePdf: (id: number) =>
    api<{ pdfUrl: string }>(`/quotations/${id}/generate-pdf`, { method: 'POST' }),
};

// ============ DELIVERY ORDERS ============
export const deliveryOrders = {
  list: (q: { search?: string; status?: DeliveryOrder['status'] } = {}) =>
    api<Envelope<DeliveryOrder>>(`/delivery-orders${qs(q)}`),
  get: (id: number) => api<DeliveryOrder>(`/delivery-orders/${id}`),
  create: (body: Partial<DeliveryOrder>) =>
    api<DeliveryOrder>('/delivery-orders', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<DeliveryOrder>) =>
    api<DeliveryOrder>(`/delivery-orders/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setStatus: (id: number, status: DeliveryOrder['status']) =>
    api<DeliveryOrder>(`/delivery-orders/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  remove: (id: number) => api<void>(`/delivery-orders/${id}`, { method: 'DELETE' }),
  generatePdf: (id: number) => api<{ pdfUrl: string }>(`/delivery-orders/${id}/generate-pdf`, { method: 'POST' }),
};

// ============ LEADS ============
export const leads = {
  list: (q: { search?: string; status?: Lead['status'] } = {}) => api<Envelope<Lead>>(`/leads${qs(q)}`),
  get: (id: number) => api<Lead>(`/leads/${id}`),
  create: (body: Partial<Lead>) => api<Lead>('/leads', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Lead>) => api<Lead>(`/leads/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setStatus: (id: number, status: Lead['status']) =>
    api<Lead>(`/leads/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  remove: (id: number) => api<void>(`/leads/${id}`, { method: 'DELETE' }),
};

// ============ PDI REQUESTS ============
export const pdiRequests = {
  list: (q: { status?: PdiRequest['status'] } = {}) => api<Envelope<PdiRequest>>(`/pdi-requests${qs(q)}`),
  get: (id: number) => api<PdiRequest>(`/pdi-requests/${id}`),
  create: (body: Partial<PdiRequest>) => api<PdiRequest>('/pdi-requests', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<PdiRequest>) =>
    api<PdiRequest>(`/pdi-requests/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/pdi-requests/${id}`, { method: 'DELETE' }),
};

// ============ OFF-HIRE RECORDS ============
export const offHireRecords = {
  list: (q: { page?: number; limit?: number } = {}) => api<Envelope<OffHireRecord>>(`/off-hire-records${qs(q)}`),
  get: (id: number) => api<OffHireRecord>(`/off-hire-records/${id}`),
  create: (body: Partial<OffHireRecord>) =>
    api<OffHireRecord>('/off-hire-records', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<OffHireRecord>) =>
    api<OffHireRecord>(`/off-hire-records/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/off-hire-records/${id}`, { method: 'DELETE' }),
};

// ============ MECHANICS ============
export const mechanics = {
  list: () => api<Mechanic[]>('/mechanics'),
  create: (body: Partial<Mechanic>) => api<Mechanic>('/mechanics', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Mechanic>) => api<Mechanic>(`/mechanics/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/mechanics/${id}`, { method: 'DELETE' }),
};

// ============ PRIME MOVERS ============
export const primeMovers = {
  list: () => api<PrimeMover[]>('/prime-movers'),
  create: (body: Partial<PrimeMover>) => api<PrimeMover>('/prime-movers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<PrimeMover>) => api<PrimeMover>(`/prime-movers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/prime-movers/${id}`, { method: 'DELETE' }),
};

// ============ RATES ============
export const rates = {
  list: (q: { tier?: string } = {}) => api<RateTier[]>(`/rates${qs(q)}`),
  create: (body: Partial<RateTier>) => api<RateTier>('/rates', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<RateTier>) => api<RateTier>(`/rates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/rates/${id}`, { method: 'DELETE' }),
  auditLog: (q: { page?: number; limit?: number } = {}) => api<Envelope<unknown>>(`/rate-audit-log${qs(q)}`),
};

// ============ CERTIFICATES ============
export const certificates = {
  list: (q: { assetName?: string; certType?: string; expiringSoon?: boolean } = {}) =>
    api<Envelope<Certificate>>(`/certificates${qs(q)}`),
  get: (id: number) => api<Certificate>(`/certificates/${id}`),
  create: (body: Partial<Certificate>) => api<Certificate>('/certificates', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Certificate>) => api<Certificate>(`/certificates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/certificates/${id}`, { method: 'DELETE' }),
};

// ============ INSURANCE ============
export const fleetInsuranceCoverage = {
  list: () => api<Envelope<FleetInsuranceCoverage>>('/fleet-insurance-coverage'),
  get: (id: number) => api<FleetInsuranceCoverage>(`/fleet-insurance-coverage/${id}`),
  create: (body: Partial<FleetInsuranceCoverage>) =>
    api<FleetInsuranceCoverage>('/fleet-insurance-coverage', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<FleetInsuranceCoverage>) =>
    api<FleetInsuranceCoverage>(`/fleet-insurance-coverage/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/fleet-insurance-coverage/${id}`, { method: 'DELETE' }),
};

export const insurancePolicies = {
  list: (q: { page?: number; limit?: number } = {}) => api<Envelope<InsurancePolicy>>(`/insurance-policies${qs(q)}`),
  get: (id: number) => api<InsurancePolicy>(`/insurance-policies/${id}`),
};

// ============ ASSET DOCUMENTS ============
export const assetDocuments = {
  list: () => api<Envelope<AssetDocument>>('/asset-documents'),
  get: (id: number) => api<AssetDocument>(`/asset-documents/${id}`),
  create: (body: Partial<AssetDocument>) => api<AssetDocument>('/asset-documents', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/asset-documents/${id}`, { method: 'DELETE' }),
};

// ============ STAFF ============
export const staff = {
  list: (q: { search?: string; role?: string } = {}) => api<Envelope<Staff>>(`/staff${qs(q)}`),
  get: (id: number) => api<Staff>(`/staff/${id}`),
  create: (body: Partial<Staff>) => api<Staff>('/staff', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Staff>) => api<Staff>(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/staff/${id}`, { method: 'DELETE' }),
};

// ============ REVENUE ============
export const revenue = {
  list: (q: { year?: number } = {}) => api<RevenueRow[]>(`/revenue${qs(q)}`),
  create: (body: Partial<RevenueRow>) => api<RevenueRow>('/revenue', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<RevenueRow>) => api<RevenueRow>(`/revenue/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/revenue/${id}`, { method: 'DELETE' }),
};

// ============ PM INVOICES ============
export const pmInvoices = {
  list: (q: { page?: number; limit?: number } = {}) => api<Envelope<PmInvoice>>(`/pm-invoices${qs(q)}`),
  get: (id: number) => api<PmInvoice>(`/pm-invoices/${id}`),
  create: (body: Partial<PmInvoice>) => api<PmInvoice>('/pm-invoices', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<PmInvoice>) => api<PmInvoice>(`/pm-invoices/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/pm-invoices/${id}`, { method: 'DELETE' }),
  lineItems: (id: number) => api<Array<{ id: number; description: string; quantity: number; unitPrice: number; totalPrice: number }>>(`/pm-invoices/${id}/line-items`),
  addLineItem: (id: number, body: { description: string; quantity?: number; unitPrice?: number; totalPrice?: number }) =>
    api<{ id: number }>(`/pm-invoices/${id}/line-items`, { method: 'POST', body: JSON.stringify(body) }),
  updateLineItem: (lineItemId: number, body: Partial<{ description: string; quantity: number; unitPrice: number; totalPrice: number }>) =>
    api<void>(`/pm-invoices/line-items/${lineItemId}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeLineItem: (lineItemId: number) => api<void>(`/pm-invoices/line-items/${lineItemId}`, { method: 'DELETE' }),
};

// ============ OT ============
export const otSubmissions = {
  list: (q: { page?: number; limit?: number } = {}) => api<Envelope<OtSubmission>>(`/ot-submissions${qs(q)}`),
  get: (id: number) => api<OtSubmission>(`/ot-submissions/${id}`),
  create: (body: Partial<OtSubmission>) => api<OtSubmission>('/ot-submissions', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<OtSubmission>) => api<OtSubmission>(`/ot-submissions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/ot-submissions/${id}`, { method: 'DELETE' }),
};

// ============ SALES KNOWLEDGE ============
export const salesKnowledge = {
  list: (q: { page?: number; limit?: number } = {}) => api<Envelope<SalesKnowledge>>(`/sales-knowledge${qs(q)}`),
  create: (body: Partial<SalesKnowledge>) => api<SalesKnowledge>('/sales-knowledge', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<SalesKnowledge>) => api<SalesKnowledge>(`/sales-knowledge/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/sales-knowledge/${id}`, { method: 'DELETE' }),
};

// ============ SETTINGS ============
export const settings = {
  list: () => api<Setting[]>('/settings'),
  get: (key: string) => api<Setting>(`/settings/${encodeURIComponent(key)}`),
  upsert: (key: string, value: string, description?: string) =>
    api<Setting>(`/settings/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ value, description }) }),
};

// Convenience namespace export
export const rsme = {
  assets, fleet, orders, customers, contacts, repairTickets, quotations,
  deliveryOrders, leads, pdiRequests, offHireRecords, mechanics, primeMovers,
  rates, certificates, fleetInsuranceCoverage, insurancePolicies, assetDocuments,
  staff, revenue, pmInvoices, otSubmissions, salesKnowledge, settings,
};
